import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldImperativeHandle,
} from "@/components/@types";
import { FieldDefinitionButtonType } from "@/components/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldButtonValue<T extends FieldDefinitionButtonType<T>>({
  events,
  valueOptions,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionButtonType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const { text, isDisabled } = valueOptions ?? {
    text: "Button",
    isDisabled: false,
  };
  const buttonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        buttonRef.current?.focus();
      },
      unsetFocus: () => {
        buttonRef.current?.blur();
      },
    }),
    [],
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    events?.onChangeField?.(true);
  };

  const handleBlur = () => {
    events?.onSubmitField?.();
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: `0 ${"4px"}`,
      }}
    >
      <button
        ref={buttonRef}
        data-testid={`field-button-${text}`}
        style={{
          ...(typographyStyles["body-sm"] as React.CSSProperties),
          fontSize: "0.75rem",
          padding: `${"4px"} ${"8px"}`,
          borderRadius: "calc(var(--radius) - 4px)",
          border: "none",
          backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          width: "100%",
          transition: "all 0.2s ease-in-out",
          ...(!isDisabled && {
            "&:hover": {
              backgroundColor:
                "color-mix(in oklab, var(--secondary) 80%, transparent)",
            },
          }),
        }}
        disabled={isDisabled}
        onClick={handleClick}
        onBlur={handleBlur}
      >
        {text}
      </button>
    </div>
  );
}

function FieldButtonSkeleton() {
  return (
    <Skeleton
      variant="text"
      level="body-md"
      style={{
        width: "100%",
        height: 32,
        borderRadius: "calc(var(--radius) - 4px)",
      }}
    />
  );
}

export function FieldButton<T extends FieldDefinitionButtonType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionButtonType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldButtonSkeleton />;
  }

  return <FieldButtonValue {...props} ref={ref} />;
}
